import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import {
  FileText,
  Download,
  Printer,
  X,
  Building2,
  User,
  MapPin,
  Calendar
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";

const LetterEditorModal = ({ open, onClose, client }) => {
  const [loading, setLoading] = useState(true);
  const [letterData, setLetterData] = useState({
    company_name: "",
    date: "",
    recipient_name: "",
    recipient_address: "",
    recipient_city: "",
    subject: "",
    body: "",
    footer: ""
  });

  useEffect(() => {
    if (open && client) {
      fetchLetterData();
    }
  }, [open, client]);

  const fetchLetterData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API + "/clients/" + client.client_id + "/letter", {
        withCredentials: true
      });
      
      const letter = response.data;
      setLetterData({
        company_name: letter.company_name || "",
        date: letter.date || "",
        recipient_name: letter.client.nome + " " + letter.client.cognome,
        recipient_address: letter.client.indirizzo || "",
        recipient_city: (letter.client.cap || "") + " " + (letter.client.comune || "") + " (" + (letter.client.provincia || "") + ")",
        subject: letter.subject || "",
        body: letter.body || "",
        footer: letter.footer || ""
      });
    } catch (error) {
      console.error("Errore caricamento lettera:", error);
      toast.error("Errore nel caricamento della lettera");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setLetterData(prev => ({ ...prev, [field]: value }));
  };

  const generatePDF = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(getLetterHTML());
    printWindow.document.close();
    
    // Auto-trigger print dialog for PDF save
    setTimeout(() => {
      printWindow.print();
    }, 500);
    
    toast.success("Usa 'Salva come PDF' nella finestra di stampa");
  };

  const printLetter = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(getLetterHTML());
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const getLetterHTML = () => {
    return "<!DOCTYPE html><html><head><title>Lettera Commerciale - " + letterData.recipient_name + "</title><style>body{font-family:'Times New Roman',serif;max-width:800px;margin:40px auto;padding:40px;line-height:1.6}.header{text-align:right;margin-bottom:40px;border-bottom:2px solid #333;padding-bottom:20px}.company-name{font-size:24px;font-weight:bold;color:#1e40af}.date{color:#666;margin-top:10px}.recipient{margin-bottom:30px}.recipient p{margin:2px 0}.subject{font-weight:bold;margin:30px 0;text-transform:uppercase}.body{text-align:justify;white-space:pre-line}.footer{margin-top:50px;text-align:center;color:#666;font-size:12px;border-top:1px solid #ddd;padding-top:20px}@media print{body{margin:0;padding:20px}}</style></head><body><div class='header'><div class='company-name'>" + letterData.company_name + "</div><div class='date'>" + letterData.date + "</div></div><div class='recipient'><p><strong>Spett.le</strong></p><p>" + letterData.recipient_name + "</p><p>" + letterData.recipient_address + "</p><p>" + letterData.recipient_city + "</p></div><div class='subject'>Oggetto: " + letterData.subject + "</div><div class='body'>" + letterData.body + "</div><div class='footer'>" + letterData.footer + "</div></body></html>";
  };

  const previewLetter = () => {
    const previewWindow = window.open("", "_blank");
    previewWindow.document.write(getLetterHTML());
    previewWindow.document.close();
  };

  if (!client) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Modifica Lettera Commerciale
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Header Section */}
            <div className="bg-slate-50 rounded-xl p-4 space-y-4">
              <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Intestazione
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Nome Azienda</Label>
                  <Input
                    id="company_name"
                    value={letterData.company_name}
                    onChange={(e) => handleChange("company_name", e.target.value)}
                    placeholder="Nome della tua azienda"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date">Data</Label>
                  <Input
                    id="date"
                    value={letterData.date}
                    onChange={(e) => handleChange("date", e.target.value)}
                    placeholder="01/01/2026"
                  />
                </div>
              </div>
            </div>

            {/* Recipient Section */}
            <div className="bg-blue-50 rounded-xl p-4 space-y-4">
              <h3 className="font-semibold text-blue-700 flex items-center gap-2">
                <User className="w-4 h-4" />
                Destinatario
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="recipient_name">Nome e Cognome</Label>
                  <Input
                    id="recipient_name"
                    value={letterData.recipient_name}
                    onChange={(e) => handleChange("recipient_name", e.target.value)}
                    placeholder="Mario Rossi"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="recipient_address">Indirizzo</Label>
                  <Input
                    id="recipient_address"
                    value={letterData.recipient_address}
                    onChange={(e) => handleChange("recipient_address", e.target.value)}
                    placeholder="Via Roma 1"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="recipient_city">CAP, Città (Provincia)</Label>
                  <Input
                    id="recipient_city"
                    value={letterData.recipient_city}
                    onChange={(e) => handleChange("recipient_city", e.target.value)}
                    placeholder="00100 Roma (RM)"
                  />
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="bg-amber-50 rounded-xl p-4 space-y-4">
              <h3 className="font-semibold text-amber-700 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Contenuto Lettera
              </h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="subject">Oggetto</Label>
                  <Input
                    id="subject"
                    value={letterData.subject}
                    onChange={(e) => handleChange("subject", e.target.value)}
                    placeholder="Comunicazione Commerciale"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="body">Corpo della Lettera</Label>
                  <Textarea
                    id="body"
                    value={letterData.body}
                    onChange={(e) => handleChange("body", e.target.value)}
                    placeholder="Scrivi il contenuto della lettera..."
                    className="min-h-[250px] font-serif"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="footer">Footer</Label>
                  <Input
                    id="footer"
                    value={letterData.footer}
                    onChange={(e) => handleChange("footer", e.target.value)}
                    placeholder="Documento generato il..."
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4 mr-2" />
            Chiudi
          </Button>
          <Button variant="outline" onClick={previewLetter} disabled={loading}>
            <FileText className="w-4 h-4 mr-2" />
            Anteprima
          </Button>
          <Button variant="outline" onClick={printLetter} disabled={loading}>
            <Printer className="w-4 h-4 mr-2" />
            Stampa
          </Button>
          <Button onClick={generatePDF} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
            <Download className="w-4 h-4 mr-2" />
            Scarica PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LetterEditorModal;
